/**
 * useAvailableWidgets Hook (DRR-048)
 *
 * Fetches available widgets from the backend widget registry.
 * This is the single source of truth for widget metadata.
 *
 * Frontend registry (registry.tsx) should ONLY contain component mappings.
 * All metadata (name, description, category, props) comes from backend.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/** Widget category (matches backend) */
export type WidgetCategory =
  | 'CORE'
  | 'ACCOUNTING'
  | 'FHIR'
  | 'INVENTORY'
  | 'PAYROLL';

/** Widget definition from backend */
export interface WidgetDefinition {
  /** Widget component name (PascalCase) */
  name: string;
  /** Human-readable display name */
  displayName: string;
  /** Widget category */
  category: WidgetCategory;
  /** Description for admin UI */
  description?: string;
  /** Supported field types this widget can handle */
  supportedTypes: string[];
  /** JSON Schema for widget-specific props */
  propsSchema?: string;
  /** Whether this widget requires lazy loading */
  lazy: boolean;
  /** Subgraphs that can use this widget (empty = all) */
  allowedSubgraphs: string[];
  /** Whether this widget handles life-critical data */
  lifeCriticalCapable: boolean;
  /** Component import path (for frontend reference) */
  importPath?: string;
}

/** Hook return type */
export interface UseAvailableWidgetsResult {
  widgets: WidgetDefinition[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  /** Get widgets filtered by category */
  getByCategory: (category: WidgetCategory) => WidgetDefinition[];
  /** Get widgets available for a subgraph */
  getForSubgraph: (subgraphId: string) => WidgetDefinition[];
  /** Get a widget by name */
  getWidget: (name: string) => WidgetDefinition | undefined;
  /** Get life-critical capable widgets */
  getLifeCriticalWidgets: () => WidgetDefinition[];
}

// =============================================================================
// GRAPHQL
// =============================================================================

const GET_AVAILABLE_WIDGETS = gql`
  query GetAvailableWidgets {
    availableWidgets {
      name
      displayName
      category
      description
      supportedTypes
      propsSchema
      lazy
      allowedSubgraphs
      lifeCriticalCapable
      importPath
    }
  }
`;

const GET_WIDGETS_BY_CATEGORY = gql`
  query GetWidgetsByCategory($category: WidgetCategory!) {
    widgetsByCategory(category: $category) {
      name
      displayName
      category
      description
      supportedTypes
      propsSchema
      lazy
      allowedSubgraphs
      lifeCriticalCapable
      importPath
    }
  }
`;

const GET_WIDGETS_FOR_SUBGRAPH = gql`
  query GetWidgetsForSubgraph($subgraphId: String!) {
    widgetsForSubgraph(subgraphId: $subgraphId) {
      name
      displayName
      category
      description
      supportedTypes
      propsSchema
      lazy
      allowedSubgraphs
      lifeCriticalCapable
      importPath
    }
  }
`;

const GET_WIDGET = gql`
  query GetWidget($name: String!) {
    widget(name: $name) {
      name
      displayName
      category
      description
      supportedTypes
      propsSchema
      lazy
      allowedSubgraphs
      lifeCriticalCapable
      importPath
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch all available widgets from the backend registry
 *
 * @example
 * ```tsx
 * const { widgets, loading, getByCategory } = useAvailableWidgets();
 *
 * // Get all accounting widgets
 * const accountingWidgets = getByCategory('ACCOUNTING');
 *
 * // Get widgets for FHIR subgraph
 * const fhirWidgets = getForSubgraph('fhir');
 * ```
 */
export function useAvailableWidgets(): UseAvailableWidgetsResult {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_AVAILABLE_WIDGETS, {
    fetchPolicy: 'cache-and-network',
  });

  const widgets = useMemo((): WidgetDefinition[] => {
    if (!data?.availableWidgets) return [];
    return data?.availableWidgets ?? null;
  }, [data]);

  const getByCategory = useMemo(() => {
    return (category: WidgetCategory): WidgetDefinition[] => {
      return widgets.filter((w) => w.category === category);
    };
  }, [widgets]);

  const getForSubgraph = useMemo(() => {
    return (subgraphId: string): WidgetDefinition[] => {
      return widgets.filter(
        (w) =>
          w.allowedSubgraphs.length === 0 ||
          w.allowedSubgraphs.includes(subgraphId)
      );
    };
  }, [widgets]);

  const getWidget = useMemo(() => {
    return (name: string): WidgetDefinition | undefined => {
      return widgets.find((w) => w.name === name);
    };
  }, [widgets]);

  const getLifeCriticalWidgets = useMemo(() => {
    return (): WidgetDefinition[] => {
      return widgets.filter((w) => w.lifeCriticalCapable);
    };
  }, [widgets]);

  return {
    widgets,
    loading,
    error: error || null,
    refetch,
    getByCategory,
    getForSubgraph,
    getWidget,
    getLifeCriticalWidgets,
  };
}

/**
 * Fetch widgets for a specific category
 *
 * @example
 * ```tsx
 * const { widgets, loading } = useWidgetsByCategory('FHIR');
 * ```
 */
export function useWidgetsByCategory(category: WidgetCategory): {
  widgets: WidgetDefinition[];
  loading: boolean;
  error: Error | null;
} {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_WIDGETS_BY_CATEGORY, {
    variables: { category },
    fetchPolicy: 'cache-and-network',
  });

  const widgets = useMemo((): WidgetDefinition[] => {
    if (!data?.widgetsByCategory) return [];
    return data?.widgetsByCategory ?? null;
  }, [data]);

  return { widgets, loading, error: error || null };
}

/**
 * Fetch widgets for a specific subgraph
 *
 * @example
 * ```tsx
 * const { widgets, loading } = useWidgetsForSubgraph('accounting');
 * ```
 */
export function useWidgetsForSubgraph(subgraphId: string): {
  widgets: WidgetDefinition[];
  loading: boolean;
  error: Error | null;
} {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_WIDGETS_FOR_SUBGRAPH, {
    variables: { subgraphId },
    fetchPolicy: 'cache-and-network',
  });

  const widgets = useMemo((): WidgetDefinition[] => {
    if (!data?.widgetsForSubgraph) return [];
    return data?.widgetsForSubgraph ?? null;
  }, [data]);

  return { widgets, loading, error: error || null };
}

/**
 * Fetch a single widget by name
 *
 * @example
 * ```tsx
 * const { widget, loading } = useWidget('GlAccountPicker');
 * ```
 */
export function useWidget(name: string): {
  widget: WidgetDefinition | null;
  loading: boolean;
  error: Error | null;
} {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_WIDGET, {
    variables: { name },
    skip: !name,
    fetchPolicy: 'cache-and-network',
  });

  return {
    widget: data?.widget || null,
    loading,
    error: error || null,
  };
}

export default useAvailableWidgets;
